import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Dashboard/Sidebar';
import Header from '../../components/Dashboard/Header';
import Footer from '../../components/Dashboard/Footer';
import { createDefinition, createVersion } from '../../utils/canvasApi';
import { ArrowLeft, ArrowRight, Layers, Loader2, CheckCircle2 } from 'lucide-react';

const DCODE_PRIMARY = '#75c4c0';
const DCODE_DANGER = '#f88381';

const SUGGESTIONS = [
  'Architecture',
  'Microservices architecture',
  'Client-server architecture',
  'MyArchitecture',
  'Architecture definition',
];

const NEXT_STEPS = [
  'A v1 draft is created automatically',
  'You\'ll be taken to the canvas to define your components and rules',
  'Assign nodes, set dependency rules, then publish when ready',
];

const ArchitectureLayerWizard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef(null);

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeSidebarItem, setActiveSidebarItem] = useState(3);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);

  const [definitionName, setDefinitionName] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const projectId = location.state?.projectId || localStorage.getItem('selectedProjectId');
  const isLargeScreen = windowWidth >= 992;
  const canCreate = definitionName.trim().length > 0;

  useEffect(() => { document.title = 'DCode | New architecture definition'; }, []);
  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setSidebarOpen(window.innerWidth >= 992);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getSidebarWidth = () => (!sidebarOpen ? '0px' : sidebarCollapsed ? '80px' : '280px');
  const toggleSidebar = () => setSidebarOpen(v => !v);
  const toggleUserDropdown = () => setUserDropdownOpen(v => !v);

  const handleCreate = useCallback(async () => {
    const trimmedName = definitionName.trim();
    if (!trimmedName) return;
    setSaving(true);
    setErrorMessage(null);
    try {
      const def = await createDefinition(projectId, { name: trimmedName });
      await createVersion(projectId, def.id, {});
      navigate(`/project/architecture-compliance/${def.id}/edit`, {
        state: { projectId, definitionName: trimmedName },
      });
    } catch (err) {
      if (err?.response?.status === 409) {
        setErrorMessage(`A definition named "${trimmedName}" already exists. Please choose a different name.`);
      } else {
        setErrorMessage(err?.response?.data?.error || err?.message || 'Failed to create definition. Please try again.');
      }
      setSaving(false);
    }
  }, [projectId, definitionName, navigate]);

  return (
    <div className="d-flex flex-column min-vh-100" style={{ background: '#f8fafc' }}>

      <div style={{ marginLeft: isLargeScreen ? getSidebarWidth() : '0px', transition: 'margin-left 0.4s', position: 'relative', zIndex: 1030 }}>
        <Header
          sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar}
          isCollapsed={sidebarCollapsed}
          userDropdownOpen={userDropdownOpen} toggleUserDropdown={toggleUserDropdown}
          pageTitle="New architecture definition"
        />
      </div>

      <Sidebar
        sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar}
        activeItem={activeSidebarItem} onItemClick={setActiveSidebarItem}
        onCollapseChange={c => setSidebarCollapsed(c)} initialCollapsed={true}
      />

      {sidebarOpen && !isLargeScreen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1040 }}
          onClick={toggleSidebar}
        />
      )}

      <main style={{ marginLeft: isLargeScreen ? getSidebarWidth() : '0px', paddingTop: '70px', minHeight: 'calc(100vh - 140px)' }}>
        <div style={{ maxWidth: 640, margin: '40px auto', padding: '0 16px' }}>

          {/* Icon + title */}
          <div className="text-center mb-4">
            <div
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 56, height: 56, borderRadius: '50%',
                background: `${DCODE_PRIMARY}20`, marginBottom: 16,
              }}
            >
              <Layers size={26} style={{ color: DCODE_PRIMARY }} />
            </div>
            <h2 className="fw-bold mb-1" style={{ fontSize: 22, color: '#0f172a' }}>
              New architecture definition
            </h2>
            <p className="text-muted mb-0" style={{ fontSize: 14 }}>
              Define the components and rules that govern your project's structure.
            </p>
          </div>

          {/* Main card */}
          <div className="card border-0 shadow-sm" style={{ borderRadius: 14 }}>

            {/* Teal accent bar */}
            <div style={{ height: 4, background: `linear-gradient(90deg, ${DCODE_PRIMARY}, #5eead4)`, borderRadius: '14px 14px 0 0' }} />

            <div className="card-body p-4">

              {/* Name input */}
              <div className="mb-4">
                <label style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', display: 'block', marginBottom: 6 }}>
                  Definition name <span style={{ color: DCODE_DANGER }}>*</span>
                </label>
                <input
                  ref={inputRef}
                  type="text"
                  value={definitionName}
                  onChange={e => { setDefinitionName(e.target.value); setErrorMessage(null); }}
                  onKeyDown={e => { if (e.key === 'Enter' && canCreate && !saving) handleCreate(); }}
                  placeholder="e.g. Clean Architecture, Microservice Boundaries…"
                  style={{
                    width: '100%', padding: '11px 14px', fontSize: 14,
                    border: `1.5px solid ${errorMessage ? DCODE_DANGER : '#e2e8f0'}`,
                    borderRadius: 8, outline: 'none', color: '#1e293b',
                    boxSizing: 'border-box', transition: 'border-color 0.15s',
                    background: '#fff',
                  }}
                  onFocus={e => { if (!errorMessage) e.target.style.borderColor = DCODE_PRIMARY; }}
                  onBlur={e => { if (!errorMessage) e.target.style.borderColor = '#e2e8f0'; }}
                />
                {errorMessage && (
                  <p style={{ fontSize: 12, color: DCODE_DANGER, marginTop: 6, marginBottom: 0 }}>
                    {errorMessage}
                  </p>
                )}
              </div>

              {/* Suggestion chips */}
              <div className="mb-4">
                <p style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                  Quick suggestions
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {SUGGESTIONS.map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => { setDefinitionName(s); setErrorMessage(null); inputRef.current?.focus(); }}
                      style={{
                        padding: '5px 14px', fontSize: 13, borderRadius: 20,
                        border: `1px solid ${definitionName === s ? DCODE_PRIMARY : '#e2e8f0'}`,
                        background: definitionName === s ? `${DCODE_PRIMARY}15` : '#f8fafc',
                        color: definitionName === s ? '#0f766e' : '#475569',
                        cursor: 'pointer', transition: 'all 0.15s', fontWeight: definitionName === s ? 600 : 400,
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <hr style={{ borderColor: '#f1f5f9', margin: '0 0 20px' }} />

              {/* What happens next */}
              <div>
                <p style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>
                  What happens next
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {NEXT_STEPS.map((step, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <CheckCircle2 size={16} style={{ color: DCODE_PRIMARY, flexShrink: 0, marginTop: 1 }} />
                      <span style={{ fontSize: 13, color: '#475569', lineHeight: 1.5 }}>{step}</span>
                    </div>
                  ))}
                </div>
              </div>

            </div>

            {/* Footer */}
            <div
              className="card-footer d-flex justify-content-between align-items-center"
              style={{ background: '#f8fafc', borderTop: '1px solid #f1f5f9', borderRadius: '0 0 14px 14px', padding: '16px 24px' }}
            >
              <button
                className="btn btn-outline-secondary d-flex align-items-center"
                style={{ fontSize: 14, borderRadius: 8 }}
                onClick={() => navigate('/project/architecture-compliance')}
                disabled={saving}
              >
                <ArrowLeft size={16} className="me-1" /> Cancel
              </button>

              <button
                className="btn d-flex align-items-center text-white"
                onClick={handleCreate}
                disabled={saving || !canCreate}
                style={{
                  background: canCreate ? DCODE_PRIMARY : '#cbd5e1',
                  border: 'none', borderRadius: 8, fontSize: 14,
                  padding: '8px 20px', fontWeight: 600,
                  transition: 'background 0.15s',
                  cursor: canCreate ? 'pointer' : 'not-allowed',
                }}
              >
                {saving
                  ? <><Loader2 size={16} className="me-2 animate-spin" /> Creating…</>
                  : <>Create &amp; Open <ArrowRight size={16} className="ms-2" /></>
                }
              </button>
            </div>

          </div>

        </div>
      </main>

      <Footer sidebarOpen={sidebarOpen} />
    </div>
  );
};

export default ArchitectureLayerWizard;
